/**
 * Violation-remediation derivations, BOARD GRAIN: posture buckets, the
 * owed-procedure ladder, the cluster boards - everything that folds many
 * matters into one card, where `matter.ts` answers about a single matter.
 * Moved verbatim from `violationRemediation.ts:1481-1994`, which was 1,137
 * code lines against the 1,000 limit; both halves land under it, so the size
 * gate drops its manifest entry rather than carry an allowance forever
 * (`components/portal/__tests__/fileSize.test.ts`).
 *
 * A directory, not a sibling `violationRemediationBoards.ts`: a barrel beside
 * a same-named directory is the resolution ambiguity already hit at
 * `components/portal/GetHelpModal/index.tsx`.
 */
import type { ViolationMatter } from "@/queries/violationMattersQueries";
import {
  tallyCitationStates,
  type CitationCoverage,
} from "@/components/portal/citationFile";

import type {
  AttributionDecomposition,
  ChargeCluster,
  ClockTone,
} from "./matter";
import {
  CLUSTER_MIN_SIZE,
  TERMINAL_LIFECYCLES,
  agencyCode,
  attributionTreatmentForMatter,
  clusterMattersByChargeCode,
  deriveReopenClockLabel,
  deriveRespondentStanding,
  formatClockDate,
  formatDateSpan,
  formatYearSpan,
  isActThisMonth,
  isLiveMatter,
  matterAnchorDate,
  matterSplitContributionUsd,
  mostCommon,
  parseDateOnly,
  uniqueShort,
} from "./matter";

// Posture buckets - one per cluster matter, from its reopen-clock kind plus
// attribution treatment. Total: every live cluster matter lands in exactly
// one, so the bucket sums reconcile to the card hero (asserted in test).

export type PostureBucketKind =
  | "near_automatic"
  | "with_excuse"
  | "beyond_reopen"
  | "reopen_won" // PR-V4 — DEFAULTED + re-scheduled hearing (reopen granted)
  | "adjudicated_owing" // PR-V4 — appeared, found in violation (appeal track)
  | "under_review"
  | "new"
  | "other";

/** Bucket for one matter. ATTRIBUTION UNCERTAINTY DOMINATES THE REOPEN CLOCK:
 *  a triage/prior-owner/excluded matter never lands in a reopen bucket, since
 *  you cannot advise a reopen on a ticket whose liability is unsettled.
 *  Owner-side routes by `deriveReopenClockLabel`'s `kind`. */
export function classifyPostureBucket(
  matter: ViolationMatter,
  now: Date = new Date(),
): PostureBucketKind {
  const treatment = attributionTreatmentForMatter(matter);
  // prior_owner + triage → "under review" (the spec's HPD-respondent bucket).
  if (treatment === "triage" || treatment === "prior_owner") {
    return "under_review";
  }
  // Excluded = settled "not yours", never a reopen action. Stays on the books
  // (the city ledger is attribution-blind) but drops to the residual.
  if (treatment === "excluded") return "other";

  // Owner-side → route by the reopen/appeal clock category.
  const clock = deriveReopenClockLabel(matter, now);
  switch (clock?.kind) {
    case "hearing_contest_open":
      return "new";
    case "reopen_near_automatic":
      return "near_automatic";
    case "reopen_with_excuse":
    case "default_no_date":
      return "with_excuse";
    case "reopen_passed":
      return "beyond_reopen";
    // PR-V4 - these two left the residual: screen 2's owed panel needs them
    // named, and they read honestly on the cluster card too.
    case "reopened_appear":
    case "reopened_awaiting_result":
      return "reopen_won";
    case "appeal_window":
      return "adjudicated_owing";
    // Only a no-clock matter (owner-side, non-terminal, no
    // reopen/appeal/hearing basis) falls to the residual. Labeled, not dropped.
    default:
      return "other";
  }
}

export interface PostureBucket {
  kind: PostureBucketKind;
  /** Fixed per-kind label ("Reopen near-automatic", …). */
  label: string;
  /** Derived one-line note (date span / respondent / action). */
  note: string;
  tone: ClockTone;
  count: number;
  amountUsd: number;
  matters: ViolationMatter[];
}

const POSTURE_BUCKET_META: Record<
  PostureBucketKind,
  { label: string; tone: ClockTone; order: number }
> = {
  near_automatic: { label: "Reopen near-automatic", tone: "urgent", order: 0 },
  with_excuse: { label: "Reopen with excuse", tone: "caution", order: 1 },
  beyond_reopen: { label: "Beyond reopen", tone: "muted", order: 2 },
  // PR-V4 - the promoted kinds slot BEFORE under_review/new so screen 2 reads
  // in statute order (reopen → adjudicated); the pre-existing relative order
  // is unchanged.
  reopen_won: { label: "Reopen won", tone: "urgent", order: 3 },
  adjudicated_owing: { label: "Adjudicated, owing", tone: "caution", order: 4 },
  under_review: { label: "Under review", tone: "caution", order: 5 },
  new: { label: "New", tone: "urgent", order: 6 },
  other: { label: "Other open", tone: "muted", order: 7 },
};

/** Operational note per bucket, DERIVED from its matters (date spans /
 *  respondent aliases). Never legal prose. */
function describePostureBucketNote(
  kind: PostureBucketKind,
  matters: readonly ViolationMatter[],
): string {
  const span = formatDateSpan(matters);
  const spanPhrase = span ? `Defaulted ${span} · ` : "";
  switch (kind) {
    case "near_automatic":
      return `${spanPhrase}first-request reopen window still open`;
    case "with_excuse":
      return `${spanPhrase}reopen with a reasonable excuse`;
    case "beyond_reopen": {
      const years = formatYearSpan(matters);
      return `${years ? `${years} defaults · ` : ""}settle, pay, or ask about exceptions`;
    }
    case "reopen_won": {
      const hearings = matters
        .map((m) => parseDateOnly(m.hearingDate))
        .filter((d): d is Date => d != null)
        .sort((a, b) => a.getTime() - b.getTime());
      return hearings.length
        ? `Reopen granted · appear ${formatClockDate(hearings[0])} or it re-defaults`
        : `Reopen granted · appear at the re-hearing or it re-defaults`;
    }
    case "adjudicated_owing": {
      const decidedSpan = formatDateSpan(matters);
      return `${decidedSpan ? `Decided ${decidedSpan} · ` : ""}appeared, found in violation — owing`;
    }
    case "under_review": {
      const names = uniqueShort(
        matters.map((m) => m.respondentName),
        2,
      );
      const namePart = names.length ? ` — ${names.join(", ")}` : "";
      const anyPredates = matters.some(
        (m) => attributionTreatmentForMatter(m) === "prior_owner",
      );
      return anyPredates
        ? `Respondent of record${namePart} predates your ownership — verify`
        : `Whose is this${namePart}? — pending a verdict`;
    }
    case "new": {
      const hearings = matters
        .map((m) => parseDateOnly(m.hearingDate))
        .filter((d): d is Date => d != null)
        .sort((a, b) => a.getTime() - b.getTime());
      return hearings.length
        ? `Hearing ${formatClockDate(hearings[0])} · contest window open, $0 today`
        : `Contest window open, $0 today`;
    }
    default:
      return "Live — see the full ticket list";
  }
}

/** Cluster matters into the non-empty buckets, in spec order. `amountUsd` is
 *  Σ `matterSplitContributionUsd`, the on-ledger terminal-floored contribution
 *  the attribution split uses, so Σ buckets == the card hero. */
export function buildPostureBuckets(
  matters: readonly ViolationMatter[],
  now: Date = new Date(),
): PostureBucket[] {
  const groups = new Map<PostureBucketKind, ViolationMatter[]>();
  matters.forEach((m) => {
    const kind = classifyPostureBucket(m, now);
    const bucket = groups.get(kind);
    if (bucket) bucket.push(m);
    else groups.set(kind, [m]);
  });
  return Array.from(groups.entries())
    .map(([kind, ms]) => {
      const meta = POSTURE_BUCKET_META[kind];
      return {
        kind,
        label: meta.label,
        tone: meta.tone,
        note: describePostureBucketNote(kind, ms),
        count: ms.length,
        amountUsd: ms.reduce(
          (acc, m) => acc + matterSplitContributionUsd(m),
          0,
        ),
        matters: ms,
      };
    })
    .sort(
      (a, b) =>
        POSTURE_BUCKET_META[a.kind].order - POSTURE_BUCKET_META[b.kind].order,
    );
}

// Cluster CARD model - the "One problem, N tickets" card. The hero is the
// on-the-books figure (Σ contribution over the OPEN matters); the buckets
// partition it.

export interface ClusterCardModel {
  chargeCode: string;
  /** Short agency label for the eyebrow ("FDNY"). */
  agencyLabel: string;
  /** "FDNY · code BF35". */
  eyebrow: string;
  /** "One problem, 9 tickets" (open count). */
  headline: string;
  /** Representative shared description ("Fire alarm — unwarranted alarms"). */
  descriptionTitle: string;
  /** "15 tickets at this building · every open one shares charge BF35 …". */
  statText: string;
  /** Σ on-the-books contribution across the OPEN cluster matters. */
  heroUsd: number;
  /** Open (live) cluster matters - the ones the buckets aggregate. */
  openTicketCount: number;
  /** All same-charge matters at this building, incl. closed (the "15"). */
  totalTicketCount: number;
  buckets: PostureBucket[];
  /** Open cluster matter ids - seed for the get-help one-case flow + see-all. */
  matterIds: string[];
  matters: ViolationMatter[];
  /** Citation coverage over the card's OPEN matters - the set the headline
   *  counts, so "One problem, 5 tickets" and "Download N of 5" agree. Same
   *  helper the case block uses. */
  citationCoverage: CitationCoverage;
}

/** One cluster card. `cluster.matters` are the OPEN matters;  `allMatters` is
 *  the full building set, used only for the same-charge total incl. closed. */
export function buildClusterCard(
  cluster: ChargeCluster,
  allMatters: readonly ViolationMatter[],
  now: Date = new Date(),
): ClusterCardModel {
  const open = cluster.matters;
  const code = cluster.chargeCode;
  const totalTicketCount = allMatters.filter(
    (m) => (m.chargeCode || "").trim() === code,
  ).length;
  const agencyLabel = mostCommon(open.map(agencyCode));
  const descriptionTitle = mostCommon(open.map((m) => m.description));
  const heroUsd = open.reduce(
    (acc, m) => acc + matterSplitContributionUsd(m),
    0,
  );
  const eyebrow = [agencyLabel, `code ${code}`].filter(Boolean).join(" · ");
  return {
    chargeCode: code,
    agencyLabel,
    eyebrow,
    headline: `One problem, ${open.length} ticket${open.length === 1 ? "" : "s"}`,
    descriptionTitle,
    // DA-S2: same charge → LIKELY one system, so the copy keeps "confirm".
    statText: `${totalTicketCount} ticket${totalTicketCount === 1 ? "" : "s"} at this building · every open one shares charge ${code} — likely one underlying system; confirm`,
    heroUsd,
    openTicketCount: open.length,
    totalTicketCount,
    buckets: buildPostureBuckets(open, now),
    matterIds: open.map((m) => m.id),
    matters: open,
    citationCoverage: tallyCitationStates(open.map((m) => m.citationState)),
  };
}

/** All cluster cards, biggest first. `minSize` threads to
 *  `clusterMattersByChargeCode` - pass `1` for the Exposure board's BY PATTERN
 *  table, which lists single-ticket groups too. */
export function buildClusterCards(
  matters: readonly ViolationMatter[],
  now: Date = new Date(),
  minSize: number = CLUSTER_MIN_SIZE,
): ClusterCardModel[] {
  return clusterMattersByChargeCode(matters, minSize).map((c) =>
    buildClusterCard(c, matters, now),
  );
}

// ---------------------------------------------------------------------------
// Matter-count trio - "N live matters · M act this month · $X owed".
// ---------------------------------------------------------------------------

export interface MatterCountTrio {
  liveCount: number;
  actThisMonthCount: number;
  /** The owed OATH composite the attribution split computed - never
   *  re-derived. 0 with no split. */
  owedUsd: number;
}

export function deriveMatterCountTrio(
  matters: readonly ViolationMatter[],
  decomposition: AttributionDecomposition | null | undefined,
  now: Date = new Date(),
): MatterCountTrio {
  return {
    liveCount: matters.filter(isLiveMatter).length,
    actThisMonthCount: matters.filter((m) => isActThisMonth(m, now)).length,
    owedUsd: decomposition?.compositeOathOwed ?? 0,
  };
}

// Closed-history line - terminal matters collapse to "History - N closed
// tickets (2011–2025)" rather than being dropped.

export interface ClosedHistory {
  count: number;
  /** "2011–2025" | "2020" | null (no datable closed matters). */
  yearRangeLabel: string | null;
  matters: ViolationMatter[];
}

export function deriveClosedHistory(
  matters: readonly ViolationMatter[],
): ClosedHistory {
  const closed = matters.filter((m) =>
    TERMINAL_LIFECYCLES.has(m.lifecycleStatus),
  );
  // Newest first - the history list reads most-recent-closed at the top.
  const sorted = [...closed].sort((a, b) => {
    const da = matterAnchorDate(a)?.getTime() ?? 0;
    const db = matterAnchorDate(b)?.getTime() ?? 0;
    return db - da;
  });
  return {
    count: closed.length,
    yearRangeLabel: formatYearSpan(closed),
    matters: sorted,
  };
}

// PR-V4 - screen 2's owed procedural-status panel ("Where the N owed tickets
// stand", 48 RCNY 6-21): ONE charge code's OWED matters across the portfolio,
// in six buckets. Reuses the SAME `classifyPostureBucket` with screen-2 labels
// and an owed filter, so a matter's cluster-card and pattern-panel buckets
// cannot disagree.

/** The six owed procedural bucket kinds - a NAMED subset of `PostureBucketKind`
 *  ("new"/"other" are never owed panel rows). */
export type OwedProcedureKind =
  | "near_automatic"
  | "with_excuse"
  | "beyond_reopen"
  | "reopen_won"
  | "adjudicated_owing"
  | "under_review";

/** The six owed procedural buckets, in statute order, the panel renders. */
export const OWED_PROCEDURE_ORDER: readonly OwedProcedureKind[] = [
  "near_automatic",
  "with_excuse",
  "beyond_reopen",
  "reopen_won",
  "adjudicated_owing",
  "under_review",
] as const;

const OWED_PROCEDURE_SET: ReadonlySet<PostureBucketKind> = new Set(
  OWED_PROCEDURE_ORDER,
);

/** Fold a classified kind into one of the six. A "new" ($0 contest) matter is
 *  already gone via the owed filter; a residual owner-side no-clock owed
 *  matter folds into "beyond both windows", so Σ buckets === the owed total. */
const foldToOwedProcedure = (kind: PostureBucketKind): OwedProcedureKind =>
  OWED_PROCEDURE_SET.has(kind) ? (kind as OwedProcedureKind) : "beyond_reopen";

/** Screen-2 verbatim labels (PR-V4 taxonomy). The under_review label appends
 *  the respondent of record when the bucket names one - derived, never
 *  fabricated. */
const OWED_PROCEDURE_LABEL: Record<OwedProcedureKind, string> = {
  near_automatic: "Reopen near-automatic",
  with_excuse: "Reopen with reasonable excuse",
  beyond_reopen: "Beyond both windows — negotiate/settle/pay",
  reopen_won: "Reopen won — appear at re-hearing",
  adjudicated_owing: "Adjudicated, owing — appeared, found in violation",
  under_review: "Under review — respondent of record",
};

/** OWED for screen 2: on the actionable ledger with a positive balance AND not
 *  a confirmed third-party. Mirrors the BE owed subset EXACTLY (your_entities
 *  + under_review, exposure > 0), so the panel reconciles to `owedTotalUsd`. */
export const isOwedPatternMatter = (matter: ViolationMatter): boolean =>
  matterSplitContributionUsd(matter) > 0 &&
  attributionTreatmentForMatter(matter) !== "excluded";

/** The six-bucket owed panel (PR-V4 screen 2): OWED subset, the SHARED
 *  `classifyPostureBucket` with residuals folded into "beyond both windows",
 *  non-empty buckets in statute order. `amountUsd` is Σ
 *  `matterSplitContributionUsd`, so Σ buckets === `owedTotalUsd`. */
export function buildOwedProcedureBuckets(
  matters: readonly ViolationMatter[],
  now: Date = new Date(),
): PostureBucket[] {
  const owed = matters.filter(isOwedPatternMatter);
  const groups = new Map<PostureBucketKind, ViolationMatter[]>();
  owed.forEach((m) => {
    const kind = foldToOwedProcedure(classifyPostureBucket(m, now));
    const bucket = groups.get(kind);
    if (bucket) bucket.push(m);
    else groups.set(kind, [m]);
  });
  return OWED_PROCEDURE_ORDER.filter((kind) => groups.has(kind)).map((kind) => {
    const ms = groups.get(kind) as ViolationMatter[];
    const meta = POSTURE_BUCKET_META[kind];
    let label: string = OWED_PROCEDURE_LABEL[kind];
    // The label names the respondent ("…is HPD"); the note stays name-free.
    let note: string;
    if (kind === "under_review") {
      const names = uniqueShort(
        ms.map((m) => m.respondentName),
        1,
      );
      if (names.length) label = `${label} is ${names[0]}`;
      note = ms.some((m) => attributionTreatmentForMatter(m) === "prior_owner")
        ? "Predates your ownership — verify before treating as resolved"
        : "Pending a verdict — whose ticket is this?";
    } else {
      note = describePostureBucketNote(kind, ms);
    }
    return {
      kind,
      label,
      tone: meta.tone,
      note,
      count: ms.length,
      amountUsd: ms.reduce((acc, m) => acc + matterSplitContributionUsd(m), 0),
      matters: ms,
    };
  });
}

// D-1 (citation PRD, Leo-decided) - the LABELED condo-board split. Condo
// Board-of-Managers tickets (respondent = the Board, MATCHED_AGENT because the
// owner is the condo's managing agent) STAY in owed exposure - dropping rows
// is the 170-Broadway failure class - but the seam is VISIBLE: "of which $X
// sits with condo-board respondents". An annotation only; the money is never
// re-bucketed.

export interface CondoBoardSegment {
  /** Σ contribution of owner-side matters whose respondent is a condominium
   *  Board of Managers. MATCHED_AGENT-gated: a MATCHED_OWNER condo entity is
   *  the owner's own and never segments. */
  amountUsd: number;
  count: number;
}

/** The condo-board segment of owed exposure, or null when there is none - the
 *  note earns its line only when real dollars sit behind a Board respondent. */
export function deriveCondoBoardSegment(
  matters: readonly ViolationMatter[],
): CondoBoardSegment | null {
  let amountUsd = 0;
  let count = 0;
  matters.forEach((m) => {
    // Owner-side only, so a triage/prior-owner/excluded matter (whose dollars
    // sit elsewhere in the decomposition) never double-counts in.
    if (attributionTreatmentForMatter(m) !== "owner") return;
    if (deriveRespondentStanding(m)?.kind !== "board_of_managers") return;
    const usd = matterSplitContributionUsd(m);
    if (usd <= 0) return;
    amountUsd += usd;
    count += 1;
  });
  return count > 0 ? { amountUsd, count } : null;
}
